import { ProgressBarLink } from "@/components/progress-bar";

import {
  ContentSegment,
  Reference,
  getRouteForReference,
  parseNotificationContent,
} from "@/lib/notification-reference";
import { currentChartPatientId, isOnPatientChart } from "@/lib/patient-route";
import { clickableText, cx } from "@/lib/tailwind-utils";

import { UserType } from "@/types/enums/user-type";

import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";

interface ReferenceSegmentProps {
  reference: Reference;
  taskId?: string;
  newWindow?: boolean;
}

/**
 * Renders a single reference as a clickable link.
 *
 * Patient references open the peek sidebar, except when the reader is already
 * on a patient chart — there a second patient alongside the first reads as a
 * mistake, so it navigates to that patient's chart instead. Other reference
 * types always navigate.
 */
function ReferenceSegment({
  reference,
  taskId,
  newWindow = true,
}: ReferenceSegmentProps) {
  const openSidebar = useRightSidebarStore((state) => state.open);

  const route = getRouteForReference(reference.type, reference.id, { taskId });

  // On a chart already: send them straight to that patient, in this tab. The
  // point is to move, so it must not open a background tab the way other
  // reference types do.
  if (
    reference.type === "patient" &&
    isOnPatientChart() &&
    route &&
    currentChartPatientId() !== reference.id
  ) {
    return (
      <ProgressBarLink
        href={route}
        newWindow={false}
        className={cx(clickableText, "inline text-[length:inherit]")}
      >
        {reference.display}
      </ProgressBarLink>
    );
  }

  // Otherwise a patient reference peeks in the sidebar rather than navigating
  if (reference.type === "patient") {
    // Parse display name — handle "Last, First", "First Last", or single names
    const display = reference.display.trim();
    let firstName = display;
    let lastName = "";

    if (display.includes(",")) {
      // "Last, First" format
      const [last, ...rest] = display.split(",").map((s) => s.trim());
      lastName = last;
      firstName = rest.join(" ") || "";
    } else {
      const nameParts = display.split(" ");
      firstName = nameParts[0] || "";
      lastName = nameParts.slice(1).join(" ") || "";
    }

    const handleClick = () =>
      openSidebar(patientSidebarDefinition, {
        patientTiny: {
          id: reference.id,
          firstName,
          lastName,
          type: UserType.PATIENT,
        },
      });

    return (
      <span
        role="button"
        tabIndex={0}
        className={cx(
          clickableText,
          "inline cursor-pointer text-[length:inherit]",
        )}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        {reference.display}
      </span>
    );
  }

  // For other reference types, use a link
  if (route) {
    return (
      <ProgressBarLink
        href={route}
        newWindow={newWindow}
        className={cx(clickableText, "inline text-[length:inherit]")}
      >
        {reference.display}
      </ProgressBarLink>
    );
  }

  // If no route available, render as bold text
  return <span className="font-semibold">{reference.display}</span>;
}

interface NotificationReferenceContentProps {
  content: string;
  className?: string;
  taskId?: string;
  newWindow?: boolean;
}

/**
 * Renders notification content with embedded references as clickable links.
 *
 * Content format: "{{type:display:id}}" where:
 * - type: patient, provider, event, call, message, chat, form, invoice, payment, etc.
 * - display: The text to display
 * - id: The ID of the referenced object
 *
 * Example: "{{patient:John Smith:pat_abc123}} scheduled an appointment for {{event:Jan 15 at 2:00 PM:evt_xyz789}}"
 */
export function NotificationReferenceContent({
  content,
  className,
  taskId,
  newWindow = true,
}: NotificationReferenceContentProps) {
  const segments = parseNotificationContent(content);

  if (segments.length === 0) {
    return null;
  }

  return (
    <p className={cx("whitespace-pre-wrap", className)}>
      {segments.map((segment: ContentSegment, index: number) => {
        if (segment.type === "text") {
          return <span key={index}>{segment.value}</span>;
        }
        return (
          <ReferenceSegment
            key={index}
            reference={segment.reference}
            taskId={taskId}
            newWindow={newWindow}
          />
        );
      })}
    </p>
  );
}
